"use client";
import "@/components/help/help-article.css";
import "@/components/ui/atoms/categories/data-display.css";

import "@/components/ui/atoms/categories/layout.css";
import { FluidMark } from "@/components/ui/atoms/FluidMark";
import { LinkedinIcon as Linkedin } from "@/components/ui/atoms/SocialIcons";
import { Button } from "@/components/ui/shadcn/button";
import { Separator } from "@/components/ui/shadcn/separator";
import { copyTextToClipboard } from "@/lib/browser/clipboard";
import { routes } from "@/lib/routing/routes";
import { Check, Copy, Mail } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { memo, useState } from "react";

export const HelpArticle = memo(function HelpArticle(
    { category, title, description, toc = [], children }: {
        category: string;
        title: string;
        description?: string;
        toc?: {
            id: string;
            title: string;
            level?: number;
        }[];
        children: React.ReactNode;
    }
) {
    const [copied, setCopied] = useState(false);

    const handleCopyPage = async () => {
        const content = document.querySelector("[data-article-content]")?.textContent || "";
        const didCopy = await copyTextToClipboard(content);
        if (didCopy) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="dw-help-article" data-component="HelpArticle" data-location="src/components/help/HelpArticle.tsx">
            {/* Main Article Content */}
            <div className="dw-help-article__content">
                {/* Breadcrumb/Category */}
                <div className="dw-help-article__category">
                    <span className="dw-help-article__category-label dw-eyebrow">{category}</span>
                </div>

                {/* Article Content */}
                <div className="dw-help-article__body" data-article-content>
                    <h1 className="dw-help-article__title dw-text-kpi" data-tone="foreground">{title}</h1>

                    {description && <p className="dw-help-article__description dw-text-lead" data-tone="muted">{description}</p>}

                    <div className="dw-help-article__children">{children}</div>

                    {/* Author Section */}
                    <div className="dw-help-article__author-divider">
                        <Link href="https://www.linkedin.com/in/kane-traylor-4994a622b/" target="_blank" rel="noopener noreferrer" className="dw-help-article__author-link">
                            <div className="dw-help-article__author-avatar">
                                <Image src="/showcase/KaneT.jpg" alt="Kane Traylor" fill className="dw-help-article__author-img" />
                            </div>
                            <div className="dw-help-article__author-info">
                                <p className="dw-help-article__author-label dw-eyebrow">Authored by</p>
                                <p className="dw-help-article__author-name dw-text-label" data-tone="foreground">Kane Traylor</p>
                                <p className="dw-text-caption" data-tone="muted">President / CEO, DisputeWell</p>
                            </div>
                            <Linkedin className="dw-help-article__author-icon dw-icon-box" data-icon-size="lg" data-tone="muted"/>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Right Sidebar - TOC & Actions */}
            <div className="dw-help-article__sidebar">
                <div className="dw-help-article__sidebar-sticky">
                    {/* TOC Links */}
                    {toc.length > 0 && (
                        <ul className="dw-help-article__toc dw-list">
                            {toc.map((item) => (
                                <li key={item.id}>
                                    <a href={`#${item.id}`} className="dw-help-article__toc-link dw-quiet-link dw-text-body" data-level={item.level} data-tone="muted">{item.title}</a>
                                </li>
                            ))}
                        </ul>
                    )}

                    {/* Divider */}
                    <Separator orientation="horizontal" />

                    {/* Action Links */}
                    <div className="dw-help-article__actions">
                        <Button variant="bare" size="auto" onClick={handleCopyPage} className="dw-help-article__action-link dw-quiet-link dw-text-body" data-tone="muted">
                            <div className="dw-help-article__copy-icon-wrap dw-icon-box">
                                {copied ? <Check className="dw-icon-box" data-tone="success" /> : <Copy className="dw-icon-box" />}
                            </div>
                            <span>{copied ? "Copied!" : "Copy page"}</span>
                        </Button>

                        <Link href="mailto:support@disputewell.com" className="dw-help-article__action-link dw-quiet-link dw-text-body" data-tone="muted">
                            <Mail className="dw-help-article__copy-icon dw-icon-box" />
                            <span>Share feedback</span>
                        </Link>

                        <Link href={routes.crm.dashboard} className="dw-help-article__action-link dw-quiet-link dw-text-body" data-tone="muted">
                            <FluidMark />
                            <span>Ask DisputeWell AI</span>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
});
